import { formatPrice } from '@/common/constant';
import type { Status } from '@/common/db-typebox';
import type { WineProductItem } from '@/common/domain-model/product-search';
import { capitalize } from '@/common/utils';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { useTreatyClient } from '@/lib/client';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import {
  Button,
  Popconfirm,
  Switch,
  Table,
  type TablePaginationConfig,
  message,
} from 'antd';
import type { FC } from 'react';
import { useState } from 'react';
import RatingIcon from '../_/rating.svg?react';

const StatusSwitch: FC<{
  record: WineProductItem;
}> = ({ record }) => {
  const initialStatus = record.status === 'enabled';
  const [status, setStatus] = useState(initialStatus);
  const [loading, setLoading] = useState(false);
  const client = useTreatyClient();

  const handleStatusChange = async (id: string, status: Status) => {
    setLoading(true);
    try {
      const res = await client.api.wine_product({ id }).status.patch({
        status,
      });
      if (res.data?.success) {
        setStatus(status === 'enabled');
        message.success('Status changed successfully');
      } else {
        setStatus(initialStatus);
        console.error(res);
        message.error(res.error?.value.message || 'Failed to change status');
      }
    } catch (error) {
      setStatus(initialStatus);
      console.error(error);
      message.error(`Failed to change status: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Switch
      checked={status}
      loading={loading}
      onChange={async (checked) => {
        setStatus(checked);
        await handleStatusChange(record.id, checked ? 'enabled' : 'disabled');
      }}
    />
  );
};

export const TableView: FC<{
  records: WineProductItem[];
  loading: boolean;
  pagination?: TablePaginationConfig;
  onViewDetail: (product: WineProductItem) => void;
  onEdit: (product: WineProductItem) => void;
  selectedRowKeys: string[];
  onSelectedRowKeysChange: (keys: string[]) => void;
  onDelete: (record: WineProductItem) => Promise<void>;
}> = ({
  records,
  loading,
  pagination,
  onViewDetail,
  onEdit,
  selectedRowKeys,
  onSelectedRowKeysChange,
  onDelete,
}) => {
  const { currency } = useCurrencySymbol();

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      onSelectedRowKeysChange(newSelectedRowKeys as string[]);
    },
  };

  const columns = [
    {
      title: 'STATUS',
      render: (record: WineProductItem) => (
        <StatusSwitch key={`status-switch-${record.id}`} record={record} />
      ),
    },
    {
      title: 'WINE',
      render: (record: WineProductItem) => (
        <span data-id={record.id}>{record.data.title}</span>
      ),
    },
    {
      title: 'TYPE',
      render: (record: WineProductItem) =>
        record.data.wine_type === 'other'
          ? `Other (${record.data.wine_type_other})`
          : capitalize(record.data.wine_type),
    },
    {
      title: 'REGION',
      render: (record: WineProductItem) =>
        record.data.country === 'Other'
          ? `Other (${record.data.country_other})`
          : record.data.country,
    },
    {
      title: 'PRICE',
      render: (record: WineProductItem) => (
        <div className="flex items-center flex-col gap-1">
          <span>{formatPrice(record.data.price, currency)}</span>
          {record.data.rating ? (
            <div className="flex items-center gap-0.5">
              <RatingIcon
                className="text-yellow-500 w-4 h-4"
                fill="currentColor"
              />
              <span>{record.data.rating}</span>
            </div>
          ) : (
            '-'
          )}
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (record: WineProductItem) => (
        <div className="flex items-center gap-2">
          <Button onClick={() => onViewDetail(record)}>Details</Button>
          <Button type="text" onClick={() => onEdit(record)}>
            <EditOutlined />
          </Button>
          <Popconfirm
            title="Delete wine"
            description="Are you sure you want to delete this wine?"
            onConfirm={() => onDelete(record)}
            okType="danger"
          >
            <Button type="text" danger>
              <DeleteOutlined className="text-red-500" />
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <Table
      rowKey={(record) => record.id}
      loading={loading}
      dataSource={records}
      columns={columns}
      pagination={pagination}
      rowSelection={rowSelection}
    />
  );
};
